/**
 *
 * EditCompany
 *
 */

import { useMemo } from 'react';
import { Flex, Spinner, SystemStyleObject } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ROUTES } from 'config/routes';
import { useNavigate, useParams } from 'react-router-dom';
import CompanyForm, { ICompanyForm } from 'app/components/CompanyForm';
import { useCustomToast } from 'hooks/useToast';
import { editCompany, getCompanyById, IRequestCompany } from 'services/http/company';
import { parseErrors, ResponseErrors } from 'utils/parseErrors';

const EditCompany = () => {
	const styles: Record<string, SystemStyleObject> = {
		container: {
			width: '100%',
			justifyContent: 'center',
			padding: { base: '1rem', md: '3.75rem' },
		},
	};
	const { companyId = '' } = useParams();
	const { addToast } = useCustomToast();
	const navigate = useNavigate();

	const { data: company, isLoading: isLoadingCompany } = useQuery<IRequestCompany, AxiosError<ResponseErrors>>(
		['companyId', companyId],
		() => getCompanyById(companyId),
		{
			onError: (errors: any) =>
				addToast({
					type: 'error',
					title: 'Erro!',
					description: parseErrors(errors?.response?.data),
				}),
			enabled: !!companyId,
		},
	);

	const defaultValues: ICompanyForm = useMemo(() => {
		return {
			id: String(company?.id),
			company: company?.company || '',
			state: { label: company?.state || '', value: company?.state || '' },
			city: company?.city || '',
			cep: company?.postalCode || '',
			neighborhood: company?.neighborhood || '',
			street: company?.street || '',
			number: company?.number || '',
			complement: company?.complement || '',
			logo: company?.storeLogo || '',
			responsibleName: company?.managerName || '',
			responsiblePhone: company?.managerPhone || '',
			responsibleEmail: company?.managerEmail || '',
			login: company?.autoAvaliarLogin || '',
			syonet: company?.syonetLogin || '',
			autoAvaliarCompanyName: company?.autoAvaliarCompanyName || '',
			cnpj: company?.cnpj || '',
			nisaBiCompanyName: company?.nisaBiCompanyName || '',
			nisaSyonetCompanyId: company?.nisaSyonetCompanyId || '',
		};
	}, [company]);

	const { mutate: updateCompanyMutate, isLoading: isLoadingCompanyUpdate } = useMutation<
		IRequestCompany,
		AxiosError<ResponseErrors>,
		IRequestCompany
	>(data => editCompany(data, companyId), {
		onSuccess: () => {
			addToast({
				type: 'success',
				title: 'Sucesso!',
				description: 'A Unidade foi editada com sucesso!',
			});
			navigate(ROUTES.listCompanies);
		},
		onError: errors =>
			addToast({
				type: 'error',
				title: 'Erro!',
				description: parseErrors(errors?.response?.data),
			}),
	});

	return (
		<>
			{isLoadingCompany ? (
				<Flex justifyContent="center">
					<Spinner />
				</Flex>
			) : (
				<Flex sx={styles.container}>
					<CompanyForm
						isEditing={true}
						defaultValues={defaultValues}
						isLoading={isLoadingCompanyUpdate}
						onSubmitForm={values => updateCompanyMutate(values)}
					/>
				</Flex>
			)}
		</>
	);
};

export default EditCompany;
